import { ArrowLeft, ChevronDown, HardHat } from 'lucide-react';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useAuth } from '@/nucleo/auth/useAuth';
import { cn } from '@/nucleo/utilitarios';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/ui/base/collapsible';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from '@/ui/base/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/ui/base/tooltip';
import { TegLogo } from '@/ui/TegLogo';
import logoCidade from '@/ui/assets/logo-cidade-sp-branco.png';

import { caminhoAtivoDoMenu, ehSoDre, escopoDeSme, formatarContagemBadge } from './apresentacao';
import {
  gruposVisiveis,
  veDocumentacaoPreCredenciamento,
  type AreaDeNavegacao,
  type ItemDeNavegacao,
} from './mapa';

/** Guarda quais grupos estão abertos, por área — cada área tem seu próprio conjunto. */
function chaveGruposAbertos(raizDaArea: string): string {
  return `teg-sidebar-grupos:${raizDaArea}`;
}

function lerGruposAbertos(raizDaArea: string): Record<string, boolean> {
  if (typeof window === 'undefined') {
    return {};
  }
  const guardado = window.localStorage.getItem(chaveGruposAbertos(raizDaArea));
  if (!guardado) {
    return {};
  }
  try {
    return JSON.parse(guardado) as Record<string, boolean>;
  } catch {
    return {};
  }
}

/**
 * Barra lateral de uma área, GERADA do mapa de navegação.
 *
 * Não existe lista de itens aqui: ela vem de nucleo/navegacao/mapa.ts, a mesma de
 * onde o roteador tira as rotas. É o que garante que menu e rota não divirjam
 * (CLAUDE.md, "Proibido": nada de rota sem tela nem tela fora da navegação).
 *
 * Item de fase futura aparece com um marcador de construção — a pessoa vê para
 * onde o sistema vai, e sabe de antemão que aquela tela ainda não faz nada.
 */
export function BarraLateral({ area }: { area: AreaDeNavegacao }) {
  const { pathname } = useLocation();
  const { usuario } = useAuth();
  const { state } = useSidebar();
  const colapsada = state === 'collapsed';
  const escopo = escopoDeSme(usuario?.papeis);
  const [gruposAbertos, setGruposAbertos] = useState<Record<string, boolean>>(() =>
    lerGruposAbertos(area.raiz),
  );
  const grupos = gruposVisiveis(
    area,
    usuario?.papeis,
    usuario?.credenciadoTipo,
    veDocumentacaoPreCredenciamento(usuario),
  );
  const caminhoAtivo = caminhoAtivoDoMenu(
    pathname,
    grupos.flatMap((grupo) =>
      grupo.itens.flatMap((item) => [
        item.caminho,
        ...(item.filhos ?? []).map((filho) => filho.caminho),
      ]),
    ),
    area.raiz,
  );
  const conteudo = useRef<HTMLDivElement>(null);

  /*
    O menu da Administração é mais alto que a tela: sem isto, quem abre uma
    tela lá de baixo (Ciclos de Inscrição, por exemplo) vê o item aceso fora da
    área visível da barra, e parece que a tela não está no menu. `nearest` só
    rola quando o item está escondido.
  */
  useEffect(() => {
    // Um quadro depois: o submenu do item ativo abre no mesmo ciclo, e o item
    // só existe no DOM depois que ele abre.
    const quadro = window.requestAnimationFrame(() => {
      const item = conteudo.current?.querySelector<HTMLElement>('[aria-current="page"]');
      if (item && typeof item.scrollIntoView === 'function') {
        item.scrollIntoView({ block: 'nearest' });
      }
    });
    return () => window.cancelAnimationFrame(quadro);
  }, [caminhoAtivo]);

  function alternarGrupo(titulo: string) {
    setGruposAbertos((atual) => {
      const abertoAgora = atual[titulo] ?? true;
      const proximo = { ...atual, [titulo]: !abertoAgora };
      window.localStorage.setItem(chaveGruposAbertos(area.raiz), JSON.stringify(proximo));
      return proximo;
    });
  }

  return (
    <Sidebar collapsible="icon" role="navigation" aria-label={`Menu de ${area.nome}`}>
      <SidebarHeader>
        <div className="flex items-center gap-2 px-2 py-1.5">
          <TegLogo size="sm" />
          <div className="min-w-0 group-data-[collapsible=icon]:hidden">
            <p className="truncate text-sm font-bold leading-tight">Sistema TEG</p>
            <p className="truncate text-xs text-sidebar-foreground/70">{area.nome}</p>
          </div>
        </div>
        {/*
          "Visão: Todas as DREs" / "Visão: DRE" — igual ao protótipo
          (`SmeLayout.tsx`, `escopoLabel`): diz de que recorte de dados o
          usuário está vendo, logo abaixo do título da sidebar.
        */}
        {area.raiz === '/sme' && escopo && (
          <div
            className="mx-2 mt-2 truncate rounded-md bg-sidebar-accent/40 px-2 py-1 text-[10px] text-sidebar-foreground/80 group-data-[collapsible=icon]:hidden"
            title={`Visão: ${escopo}`}
          >
            Visão: <span className="font-medium">{escopo}</span>
          </div>
        )}
      </SidebarHeader>

      <SidebarContent ref={conteudo}>
        {/*
          "Voltar ao sistema" / "Voltar ao Painel Admin" — igual ao protótipo
          (`AppSidebar.tsx`, modo admin: botão fixo no topo da sidebar pra
          sair da Administração; `SmeLayout.tsx`: mesmo botão, mas só quando
          quem está no Portal SME também é admin). Lá é alternância de modo
          dentro da mesma sidebar; aqui cada uma é área de URL própria, então
          o equivalente é um link real de volta, na mesma posição.
        */}
        {area.raiz === '/admin' && (
          <SidebarGroup className="p-1">
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Voltar ao sistema">
                    <Link to="/operacao">
                      <ArrowLeft aria-hidden="true" />
                      <span>Voltar ao sistema</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
        {area.raiz === '/sme' && usuario?.papeis.includes('ADMIN') && (
          <SidebarGroup className="p-1">
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Voltar ao Painel Admin">
                    <Link to="/admin">
                      <ArrowLeft aria-hidden="true" />
                      <span>Voltar ao Painel Admin</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {grupos.map((grupo) => {
          const aberto = colapsada || (gruposAbertos[grupo.titulo] ?? true);
          const temItemAtivo = grupo.itens.some(
            (item) =>
              item.caminho === caminhoAtivo ||
              item.filhos?.some((filho) => filho.caminho === caminhoAtivo),
          );
          return (
            <Collapsible
              key={grupo.titulo}
              open={aberto}
              onOpenChange={() => alternarGrupo(grupo.titulo)}
            >
              <SidebarGroup>
                <CollapsibleTrigger
                  tabIndex={colapsada ? -1 : undefined}
                  className={cn(
                    'flex h-8 w-full shrink-0 items-center justify-between rounded-md px-2 text-left text-xs font-medium text-sidebar-foreground outline-hidden ring-sidebar-ring transition-[margin,opacity] duration-200 ease-linear hover:bg-sidebar-accent/50 focus-visible:ring-2',
                    'group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0',
                    temItemAtivo && 'font-semibold text-orange',
                  )}
                >
                  {grupo.titulo}
                  <ChevronDown
                    className={cn(
                      'h-3.5 w-3.5 shrink-0 transition-transform duration-200',
                      !aberto && '-rotate-90',
                    )}
                    aria-hidden="true"
                  />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {grupo.itens.map((item) => (
                        <ItemDoMenu key={item.caminho} item={item} caminhoAtivo={caminhoAtivo} />
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </SidebarGroup>
            </Collapsible>
          );
        })}
      </SidebarContent>

      <SidebarFooter
        className={cn(
          'items-center border-t border-sidebar-border/30 py-3',
          colapsada ? 'justify-center px-1' : 'px-4',
        )}
      >
        <img
          src={logoCidade}
          alt="Cidade de São Paulo"
          className={cn(
            'object-contain opacity-60 transition-opacity hover:opacity-80',
            colapsada ? 'h-6 max-w-full min-w-0' : 'h-10 w-auto',
          )}
        />
      </SidebarFooter>

      {/* Trilho de recolher: alvo de clique largo na borda, além do botão do cabeçalho. */}
      <SidebarRail />
    </Sidebar>
  );
}

function ItemDoMenu({
  item,
  caminhoAtivo,
}: {
  item: ItemDeNavegacao;
  /** O único caminho aceso no menu (`caminhoAtivoDoMenu`). */
  caminhoAtivo: string | undefined;
}) {
  const Icone = item.icone;
  const ativo = item.caminho === caminhoAtivo;
  const emConstrucao = item.fase > 1;
  const { isMobile, setOpenMobile } = useSidebar();
  const { usuario } = useAuth();
  const rotulo = item.tituloParaDre && ehSoDre(usuario?.papeis) ? item.tituloParaDre : item.titulo;

  if (item.filhos && item.filhos.length > 0) {
    return <ItemDoMenuComFilhos item={item} filhos={item.filhos} caminhoAtivo={caminhoAtivo} />;
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={ativo}
        tooltip={rotulo}
        className={cn(
          'group-has-data-[sidebar=menu-badge]/menu-item:pr-10',
          ativo ? 'border-l-[3px] border-orange' : 'text-sidebar-foreground/70',
        )}
      >
        <Link
          to={item.caminho}
          onClick={() => isMobile && setOpenMobile(false)}
          aria-current={ativo ? 'page' : undefined}
        >
          <Icone aria-hidden="true" />
          <span className="flex-1 truncate">{rotulo}</span>

          {emConstrucao && (
            <Tooltip>
              <TooltipTrigger asChild>
                <HardHat
                  className="ml-auto h-3.5 w-3.5 shrink-0 text-sidebar-foreground/60"
                  aria-hidden="true"
                />
              </TooltipTrigger>
              <TooltipContent side="right">Módulo da fase {item.fase}</TooltipContent>
            </Tooltip>
          )}

          {/* Para quem usa leitor de tela, o marcador precisa ser texto. */}
          {emConstrucao && <span className="sr-only">— módulo da fase {item.fase}</span>}
        </Link>
      </SidebarMenuButton>
      {item.contagem !== undefined && (
        <SidebarMenuBadge className="rounded-full bg-sidebar-accent/70 text-[10px] text-sidebar-accent-foreground">
          {formatarContagemBadge(item.contagem)}
        </SidebarMenuBadge>
      )}
    </SidebarMenuItem>
  );
}

/**
 * Item que tem filhos: leva à própria rota E abre o submenu.
 *
 * O item é um LINK, e o chevron ao lado é um botão separado que só expande e
 * recolhe. Até 2026-09-14 o item inteiro era o gatilho de expandir, e a rota do
 * pai — os hubs de parametrização, entre outros — só se alcançava digitando o
 * endereço na barra do navegador. Isso é tela fora da navegação, o que o
 * CLAUDE.md proíbe, e reprova o critério de aceite "abre pelo menu".
 *
 * Os dois alvos ficam lado a lado, e não um dentro do outro: elemento clicável
 * aninhado dentro de outro é falha de acessibilidade, além de deixar ambíguo o
 * que o clique faz.
 *
 * Pai e filhos acendem pelo `caminhoAtivo` que a barra calcula uma vez para o
 * menu inteiro (`caminhoAtivoDoMenu`): caminho exato ou, sem ele, o prefixo mais
 * longo. Assim o detalhe de um filho (`/operacao/sorteios/:id`) acende o filho e
 * abre o submenu, e um caminho de filho que é prefixo de outro (`/termos` e
 * `/termos/vencimentos`) não acende os dois ao mesmo tempo.
 *
 * No modo colapsado (64px), "cada item mostra apenas o ícone" vale pros filhos
 * também: em vez do submenu recuado, pai e filhos aparecem soltos e achatados,
 * cada um com seu próprio ícone e tooltip — não dá pra abrir/fechar submenu
 * num modo que já não mostra rótulo nenhum.
 */
function ItemDoMenuComFilhos({
  item,
  filhos,
  caminhoAtivo,
}: {
  item: ItemDeNavegacao;
  filhos: NonNullable<ItemDeNavegacao['filhos']>;
  caminhoAtivo: string | undefined;
}) {
  const Icone = item.icone;
  const emConstrucao = item.fase > 1;
  const temFilhoAtivo = filhos.some((filho) => filho.caminho === caminhoAtivo);
  const paiAtivo = item.caminho === caminhoAtivo;
  const dentroDoSubmenu = temFilhoAtivo || paiAtivo;
  const [aberto, setAberto] = useState(dentroDoSubmenu);
  const { isMobile, setOpenMobile, state } = useSidebar();

  /*
    Entrar no hub ou em qualquer filho abre o submenu, mesmo que a pessoa o
    tenha recolhido antes: chegar numa tela e não ver onde ela fica no menu é
    desorientador. Fora do subconjunto, a escolha de quem recolheu é mantida.
  */
  useEffect(() => {
    if (dentroDoSubmenu) {
      setAberto(true);
    }
  }, [dentroDoSubmenu]);

  if (state === 'collapsed' && !isMobile) {
    const ativo = paiAtivo;
    return (
      <>
        <SidebarMenuItem>
          <SidebarMenuButton
            asChild
            isActive={ativo}
            tooltip={item.titulo}
            className={cn(
              'group-has-data-[sidebar=menu-badge]/menu-item:pr-10',
              ativo ? 'border-l-[3px] border-orange' : 'text-sidebar-foreground/70',
            )}
          >
            <Link to={item.caminho} aria-current={ativo ? 'page' : undefined}>
              <Icone aria-hidden="true" />
              <span className="flex-1 truncate">{item.titulo}</span>

              {emConstrucao && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HardHat
                      className="ml-auto h-3.5 w-3.5 shrink-0 text-sidebar-foreground/60"
                      aria-hidden="true"
                    />
                  </TooltipTrigger>
                  <TooltipContent side="right">Módulo da fase {item.fase}</TooltipContent>
                </Tooltip>
              )}

              {/* Para quem usa leitor de tela, o marcador precisa ser texto. */}
              {emConstrucao && <span className="sr-only">— módulo da fase {item.fase}</span>}
            </Link>
          </SidebarMenuButton>
          {item.contagem !== undefined && (
            <SidebarMenuBadge className="rounded-full bg-sidebar-accent/70 text-[10px] text-sidebar-accent-foreground">
              {formatarContagemBadge(item.contagem)}
            </SidebarMenuBadge>
          )}
        </SidebarMenuItem>
        {filhos.map((filho) => {
          const filhoAtivo = filho.caminho === caminhoAtivo;
          const filhoEmConstrucao = filho.fase > 1;
          const IconeFilho = filho.icone;
          return (
            <SidebarMenuItem key={filho.caminho}>
              <SidebarMenuButton
                asChild
                isActive={filhoAtivo}
                tooltip={filho.titulo}
                className={cn(
                  'group-has-data-[sidebar=menu-badge]/menu-item:pr-10',
                  filhoAtivo ? 'border-l-[3px] border-orange' : 'text-sidebar-foreground/70',
                )}
              >
                <Link to={filho.caminho} aria-current={filhoAtivo ? 'page' : undefined}>
                  <IconeFilho aria-hidden="true" />
                  <span className="flex-1 truncate">{filho.titulo}</span>

                  {filhoEmConstrucao && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HardHat
                          className="ml-auto h-3.5 w-3.5 shrink-0 text-sidebar-foreground/60"
                          aria-hidden="true"
                        />
                      </TooltipTrigger>
                      <TooltipContent side="right">Módulo da fase {filho.fase}</TooltipContent>
                    </Tooltip>
                  )}

                  {filhoEmConstrucao && (
                    <span className="sr-only">— módulo da fase {filho.fase}</span>
                  )}
                </Link>
              </SidebarMenuButton>
              {filho.contagem !== undefined && (
                <SidebarMenuBadge className="rounded-full bg-sidebar-accent/70 text-[10px] text-sidebar-accent-foreground">
                  {formatarContagemBadge(filho.contagem)}
                </SidebarMenuBadge>
              )}
            </SidebarMenuItem>
          );
        })}
      </>
    );
  }

  return (
    // `asChild`: sem isto o Collapsible do Radix renderia sua própria <div>
    // por fora do <SidebarMenuItem> (li) — filho direto de <SidebarMenu>
    // (ul), quebrando a semântica de lista (achado real, também presente no
    // protótipo — corrigido aqui, não replicado).
    <Collapsible asChild open={aberto} onOpenChange={setAberto}>
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          isActive={paiAtivo}
          tooltip={item.titulo}
          className={cn(
            paiAtivo && 'border-l-[3px] border-orange',
            !paiAtivo && temFilhoAtivo && 'font-medium text-orange',
            !paiAtivo && !temFilhoAtivo && 'text-sidebar-foreground/70',
          )}
        >
          <Link
            to={item.caminho}
            onClick={() => isMobile && setOpenMobile(false)}
            aria-current={paiAtivo ? 'page' : undefined}
          >
            <Icone aria-hidden="true" />
            <span className="flex-1 truncate">{item.titulo}</span>

            {emConstrucao && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <HardHat
                    className="h-3.5 w-3.5 shrink-0 text-sidebar-foreground/60"
                    aria-hidden="true"
                  />
                </TooltipTrigger>
                <TooltipContent side="right">Módulo da fase {item.fase}</TooltipContent>
              </Tooltip>
            )}
            {emConstrucao && <span className="sr-only">— módulo da fase {item.fase}</span>}

            {item.contagem !== undefined && (
              <span className="inline-flex h-4 min-w-[18px] items-center justify-center rounded-full bg-sidebar-accent/70 px-1.5 text-[10px] font-semibold text-sidebar-accent-foreground tabular-nums">
                {formatarContagemBadge(item.contagem)}
              </span>
            )}
          </Link>
        </SidebarMenuButton>

        {/*
          O gatilho de expandir é botão à parte, com nome próprio: quem navega
          por teclado ou leitor de tela precisa distinguir "ir para o hub" de
          "mostrar as telas do hub".
        */}
        <CollapsibleTrigger asChild>
          <SidebarMenuAction
            aria-label={`${aberto ? 'Recolher' : 'Expandir'} ${item.titulo}`}
            className="text-sidebar-foreground/70"
          >
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 shrink-0 transition-transform duration-200',
                !aberto && '-rotate-90',
              )}
              aria-hidden="true"
            />
          </SidebarMenuAction>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <SidebarMenuSub>
            {filhos.map((filho) => {
              const filhoAtivo = filho.caminho === caminhoAtivo;
              const filhoEmConstrucao = filho.fase > 1;
              const IconeFilho = filho.icone;
              return (
                <Fragment key={filho.caminho}>
                  {filho.separadorAntes && (
                    // `<li>`, nao `<div>`: e filho direto de `SidebarMenuSub`
                    // (um `<ul>`) — um `<div>` solto ali reprova o axe
                    // (list: "<ul> and <ol> must only directly contain <li>").
                    <li className="px-2 pt-3 pb-1 text-[10px] font-medium uppercase tracking-wider text-sidebar-foreground/40">
                      {filho.separadorAntes}
                    </li>
                  )}
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      asChild
                      isActive={filhoAtivo}
                      className={cn(
                        filhoAtivo ? 'border-l-[3px] border-orange' : 'text-sidebar-foreground/70',
                      )}
                    >
                      <Link
                        to={filho.caminho}
                        onClick={() => isMobile && setOpenMobile(false)}
                        aria-current={filhoAtivo ? 'page' : undefined}
                      >
                        <IconeFilho aria-hidden="true" />
                        <span className="flex-1 truncate">{filho.titulo}</span>

                        {filhoEmConstrucao && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <HardHat
                                className="h-3.5 w-3.5 shrink-0 text-sidebar-foreground/60"
                                aria-hidden="true"
                              />
                            </TooltipTrigger>
                            <TooltipContent side="right">
                              Módulo da fase {filho.fase}
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {filhoEmConstrucao && (
                          <span className="sr-only">— módulo da fase {filho.fase}</span>
                        )}

                        {filho.contagem !== undefined && (
                          <span className="ml-auto inline-flex h-4 min-w-[18px] items-center justify-center rounded-full bg-sidebar-accent/70 px-1.5 text-[10px] font-semibold text-sidebar-accent-foreground tabular-nums">
                            {formatarContagemBadge(filho.contagem)}
                          </span>
                        )}
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                </Fragment>
              );
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}
